import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtTL } from "@/components/StatusBadge";
import { Building2, TrendingUp, PackageCheck, Clock, AlertTriangle, Wallet, ShoppingCart, Loader2, CalendarClock } from "lucide-react";

const STATUS_TR = {
  requested: "Onay bekliyor", approved: "Onaylı", in_transit: "Yolda",
  completed: "Tamamlandı", rejected: "Reddedildi", cancelled: "İptal",
};

const Metric = ({ label, value, icon: Icon, cls }) => (
  <div className="rounded-lg bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
    <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500"><Icon className={`h-3.5 w-3.5 ${cls || "text-slate-400"}`} /> {label}</div>
    <p className="mt-1 text-lg font-bold text-slate-800 tabular">{value}</p>
  </div>
);

export default function SupplierSummary({ supplierId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!supplierId) return;
    setLoading(true);
    api.get(`/suppliers/${supplierId}/summary`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [supplierId]);
  const p = data?.performance || {};
  return (
    <Dialog open={!!supplierId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="supplier-summary-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex flex-wrap items-center gap-2"><Building2 className="h-5 w-5 text-brand-green" /> {data?.name || "Tedarikçi"}
            {p.score != null && (
              <span data-testid="supplier-summary-score" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${p.score >= 85 ? "bg-emerald-100 text-emerald-700 ring-emerald-200" : p.score >= 60 ? "bg-amber-100 text-amber-700 ring-amber-200" : "bg-red-100 text-red-700 ring-red-200"}`}>
                <TrendingUp className="h-3 w-3" /> Skor {p.score}/100
              </span>
            )}
          </DialogTitle>
          <DialogDescription>{data?.code ? `${data.code} · ` : ""}Tedarikçi performansı ve sipariş geçmişi</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : !data ? (
          <p className="py-8 text-center text-sm text-slate-400">Özet yüklenemedi.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Metric label="Toplam Sipariş" value={p.total_orders ?? 0} icon={ShoppingCart} cls="text-amber-500" />
              <Metric label="Tamamlanan" value={p.completed ?? 0} icon={PackageCheck} cls="text-emerald-500" />
              <Metric label="Açık Sipariş" value={p.active ?? 0} icon={Clock} cls="text-indigo-500" />
              <Metric label="Tamamlanma" value={p.completion_rate != null ? `%${p.completion_rate}` : "—"} icon={TrendingUp} cls="text-brand-green" />
              <Metric label="Sorunsuz" value={p.clean_rate != null ? `%${p.clean_rate}` : "—"} icon={PackageCheck} cls="text-emerald-500" />
              <Metric label="Zamanında" value={p.on_time_rate != null ? `%${p.on_time_rate}` : "—"} icon={Clock} cls="text-brand-green" />
              <Metric label="Farklı/Eksik" value={p.discrepancy_orders ?? 0} icon={AlertTriangle} cls="text-red-500" />
            </div>
            {data.balance_visible && (
              <div data-testid="supplier-balance" className="rounded-lg bg-amber-50 p-3 ring-1 ring-inset ring-amber-200">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-amber-700"><Wallet className="h-4 w-4" /> Cari Bakiye</span>
                  <span className={`tabular text-base font-bold ${data.balance > 0 ? "text-emerald-700" : data.balance < 0 ? "text-red-600" : "text-slate-500"}`}>
                    {fmtTL(Math.abs(data.balance || 0))} <span className="text-[10px] font-medium">{data.balance > 0 ? "(bize borçlu)" : data.balance < 0 ? "(biz borçluyuz)" : "(kapalı)"}</span>
                  </span>
                </div>
              </div>
            )}
            {data.total_purchase_value != null && (
              <p data-testid="supplier-total-volume" className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-inset ring-slate-200">Toplam satın alma hacmi: <b className="text-slate-800">{fmtTL(data.total_purchase_value)}</b></p>
            )}
            {(data.payment_term_days != null || data.balance_visible) && (
              <div data-testid="supplier-payment" className="space-y-1 rounded-lg bg-slate-50 p-3 text-xs ring-1 ring-inset ring-slate-200">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 font-medium text-slate-600"><CalendarClock className="h-4 w-4 text-slate-400" /> Ödeme Vadesi</span>
                  <span className="font-semibold text-slate-800">{data.payment_term_days ? `${data.payment_term_days} gün` : "Peşin"}</span>
                </div>
                {data.payment_due_date && (
                  <div className={`flex items-center justify-between ${data.payment_overdue ? "font-semibold text-red-600" : "text-slate-500"}`}>
                    <span>Tahmini Son Ödeme</span>
                    <span>{String(data.payment_due_date).slice(0, 10)}{data.payment_overdue ? " · VADESİ GEÇTİ" : ""}</span>
                  </div>
                )}
                {data.credit_limit != null && (
                  <div className={`flex items-center justify-between ${data.limit_exceeded ? "font-semibold text-red-600" : "text-slate-500"}`}>
                    <span>Kredi Limiti</span>
                    <span>{fmtTL(data.credit_limit)}{data.credit_available != null ? ` · Kalan ${fmtTL(data.credit_available)}` : ""}{data.limit_exceeded ? " · LİMİT AŞILDI" : ""}</span>
                  </div>
                )}
              </div>
            )}
            <div>
              <p className="overline mb-1.5">Son Siparişler</p>
              {(data.recent_orders || []).length === 0 ? (
                <p className="text-xs text-slate-400">Bu tedarikçiyle henüz satın alma siparişi yok.</p>
              ) : (
                <div className="divide-y divide-slate-100 rounded-md ring-1 ring-inset ring-slate-200">
                  {data.recent_orders.map((o) => (
                    <div key={o.transfer_no} data-testid={`supplier-order-${o.transfer_no}`} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                      <span className="font-mono font-semibold text-slate-600">{o.transfer_no}</span>
                      <span className="text-slate-400">{String(o.created_at).slice(0, 10)} · {o.lines} kalem</span>
                      <span className="flex items-center gap-2">
                        {o.value != null && <span className="tabular text-slate-600">{fmtTL(o.value)}</span>}
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{STATUS_TR[o.status] || o.status}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
